import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { MapPin, Navigation, Clock, Car, User, ShieldCheck, Flag, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtTime = (iso) =>
  iso ? new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
const fmtEta = (mins) => {
  if (typeof mins !== "number" || mins < 0) return null;
  if (mins < 120) return `~${mins} min to go`;
  const h = Math.floor(mins / 60);
  return `~${h} h ${mins % 60} min to go`;
};
const fmtAgo = (iso) => {
  if (!iso) return null;
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 90) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  return `${Math.floor(s / 3600)} h ago`;
};

function osmEmbed({ lat, lng, origin_lat, origin_lon, destination_lat, destination_lon }) {
  const pts = [
    [origin_lat, origin_lon],
    [destination_lat, destination_lon],
    [lat, lng],
  ].filter(([a, b]) => typeof a === "number" && typeof b === "number");
  if (!pts.length) return null;
  const lats = pts.map((p) => p[0]);
  const lons = pts.map((p) => p[1]);
  const pad = Math.max(0.05, (Math.max(...lats) - Math.min(...lats)) * 0.15);
  const bbox = `${Math.min(...lons) - pad},${Math.min(...lats) - pad},${Math.max(...lons) + pad},${Math.max(...lats) + pad}`;
  const marker = typeof lat === "number" && typeof lng === "number" ? `&marker=${lat},${lng}` : "";
  return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik${marker}`;
}

export default function TrackPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/track/${id}`);
      setData(r.data);
      setError("");
      return r.data;
    } catch (e) {
      setError(e.response?.status === 404 ? "notfound" : "error");
      return null;
    }
  }, [id]);

  useEffect(() => {
    let stopped = false;
    (async () => {
      const d = await load();
      if (stopped) return;
      if (d?.active) {
        timer.current = setInterval(async () => {
          const next = await load();
          if (next && !next.active && timer.current) {
            clearInterval(timer.current); // trip ended — stop live polling
            timer.current = null;
          }
        }, 10000);
      }
    })();
    return () => {
      stopped = true;
      if (timer.current) clearInterval(timer.current);
    };
  }, [load]);

  const live = !!data?.active;
  const mapSrc = data ? osmEmbed(data) : null;
  const progress = Math.min(100, Math.max(0, (data?.progress || 0) * 100));

  return (
    <div className="min-h-screen bg-[#0d0f24] text-white" data-testid="track-page">
      {/* Header */}
      <header className="mx-auto flex max-w-3xl items-center justify-between px-5 py-5">
        <Link to="/" data-testid="track-logo" className="flex items-center gap-2.5">
          <img src="/saferide-logo.png" alt="SafeRide" className="h-9 w-auto" />
        </Link>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/8 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white/70">
          <ShieldCheck className="h-3.5 w-3.5" /> SafeTrip Tracking
        </span>
      </header>

      <main className="mx-auto max-w-3xl px-5 pb-16">
        {error === "notfound" && (
          <div className="mt-10 rounded-3xl bg-white/6 p-10 text-center" data-testid="track-notfound">
            <Flag className="mx-auto h-10 w-10 text-white/40" />
            <h1 className="mt-4 font-display text-2xl font-black">Tracking link not found</h1>
            <p className="mt-2 text-sm text-white/60">This tracking link is invalid or has expired. Ask the traveller to share a new one.</p>
          </div>
        )}
        {error === "error" && (
          <div className="mt-10 rounded-3xl bg-white/6 p-10 text-center" data-testid="track-error">
            <RefreshCw className="mx-auto h-10 w-10 text-white/40" />
            <h1 className="mt-4 font-display text-2xl font-black">Tracking is temporarily unavailable</h1>
            <button data-testid="track-retry" onClick={load}
              className="mt-5 rounded-full bg-[#e4002b] px-6 py-2.5 text-sm font-bold hover:bg-[#c40024]">Try again</button>
          </div>
        )}

        {!data && !error && <p className="mt-16 text-center text-sm text-white/50">Loading trip…</p>}

        {data && (
          <>
            {/* Status + route hero */}
            <section className="mt-4 rounded-3xl bg-white p-6 text-[#12142b] shadow-2xl sm:p-8" data-testid="track-hero">
              <div className="flex items-center justify-between">
                {live ? (
                  <span data-testid="track-live-badge" className="inline-flex items-center gap-2 rounded-full bg-[#e4002b]/10 px-3.5 py-1.5 text-xs font-black uppercase tracking-wider text-[#e4002b]">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-[#e4002b]" /> Live
                  </span>
                ) : (
                  <span data-testid="track-ended-badge" className="inline-flex items-center gap-2 rounded-full bg-[#12142b]/8 px-3.5 py-1.5 text-xs font-black uppercase tracking-wider text-[#565b73]">
                    Trip has ended
                  </span>
                )}
                {data.last_update && (
                  <span className="text-xs font-medium text-[#565b73]" data-testid="track-last-update">
                    Updated {fmtAgo(data.last_update)}
                  </span>
                )}
              </div>

              <div className="mt-6 flex items-center gap-4" data-testid="track-route">
                <div className="flex flex-col items-center">
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-[#2340b5]/10"><MapPin className="h-4.5 w-4.5 text-[#2340b5]" /></span>
                  <span className="my-1 h-10 w-0.5 bg-gradient-to-b from-[#2340b5]/40 to-[#e4002b]/40" />
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-[#e4002b]/10"><Navigation className="h-4.5 w-4.5 text-[#e4002b]" /></span>
                </div>
                <div className="flex-1">
                  <p className="font-display text-xl font-black tracking-tight sm:text-2xl" data-testid="track-origin">{data.origin || "—"}</p>
                  <p className="my-3 text-xs font-semibold uppercase tracking-widest text-[#565b73]/60">
                    {data.distance_km ? `${data.distance_km} km` : "on the way"}
                  </p>
                  <p className="font-display text-xl font-black tracking-tight sm:text-2xl" data-testid="track-destination">{data.destination || "—"}</p>
                </div>
              </div>

              {live && (
                <div className="mt-6">
                  <div className="h-2 overflow-hidden rounded-full bg-[#12142b]/8" data-testid="track-progress">
                    <div className="h-full rounded-full bg-gradient-to-r from-[#2340b5] to-[#e4002b] transition-all duration-700" style={{ width: `${progress}%` }} />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs font-semibold text-[#565b73]">
                    <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Departed {fmtTime(data.started_at || data.depart_at)}</span>
                    {fmtEta(data.eta_minutes) && <span data-testid="track-eta">{fmtEta(data.eta_minutes)}</span>}
                  </div>
                </div>
              )}
              {!live && (
                <p className="mt-6 rounded-2xl bg-[#f4f6fb] p-4 text-center text-sm font-semibold text-[#565b73]" data-testid="track-ended-message">
                  This trip has ended — live location sharing is no longer active.
                </p>
              )}
            </section>

            {/* Driver & vehicle */}
            <section className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-3xl bg-white/6 p-5" data-testid="track-driver">
                <div className="flex items-center gap-3">
                  <span className="grid h-11 w-11 place-items-center rounded-full bg-[#2340b5]/25"><User className="h-5 w-5 text-white" /></span>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-white/50">Driver</p>
                    <p className="font-display text-lg font-bold">{data.driver_name || "SafeRide driver"}</p>
                  </div>
                </div>
              </div>
              <div className="rounded-3xl bg-white/6 p-5" data-testid="track-vehicle">
                <div className="flex items-center gap-3">
                  <span className="grid h-11 w-11 place-items-center rounded-full bg-[#e4002b]/20"><Car className="h-5 w-5 text-white" /></span>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-white/50">Vehicle</p>
                    <p className="font-display text-lg font-bold">{data.vehicle || "Registered vehicle"}</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Live map */}
            <section className="mt-4 overflow-hidden rounded-3xl bg-white/6" data-testid="track-map">
              {mapSrc ? (
                <iframe title="Live trip map" src={mapSrc} className="h-[380px] w-full border-0" loading="lazy" />
              ) : (
                <div className="grid h-[380px] place-items-center p-8 text-center">
                  <div>
                    <MapPin className="mx-auto h-10 w-10 text-white/30" />
                    <p className="mt-3 text-sm text-white/60">Live position will appear here as soon as the traveller's phone shares it.</p>
                  </div>
                </div>
              )}
            </section>

            <p className="mt-6 text-center text-xs text-white/40">
              Shared securely via SafeRide · only people with this link can see this trip
            </p>
          </>
        )}
      </main>
    </div>
  );
}
